import MediaCardPreviewGallery from './MediaCardPreviewGallery';

Vue.asyncComponent('cms-media-card', {
    props: {
        title: {
            type: String,
            default: '',
        },
        href: {
            type: String,
            default: '',
        },
        body: {
            type: String,
            default: '',
        },
        img: {
            type: String,
        },
        isImage: {
            type: String,
        },
        extension: {
            type: String,
        },
        active: {
            type: Boolean,
        },
        size: {
            type: String,
        },
        objectId: {
            type: String,
        },
        baseRelationString: {
            type: String,
        },
        downloadUrl: {
            type: String,
        },
        recommendedFileSize: {
            type: String,
        },
        fileSize: {
            type: [String, Number],
            default: '',
        },
        fileExists: {
            type: [String,Boolean]
        },
        previewGalleryId: {
            type: String,
        },
    },

    computed: {
        /**
         * URL used for the preview.
         *
         * PDFs receive preview=1 so the endpoint returns an inline response.
         *
         * @returns {string}
         */
        previewUrl() {
            if (!this.downloadUrl) {
                return '';
            }

            const url = new URL(
                this.downloadUrl,
                window.location.origin,
            );

            if (this.getPreviewType() === 'external') {
                url.searchParams.set('preview', '1');
            }

            return url.toString();
        },
    },
    mounted() {
        this.registerPreview();
    },
    beforeDestroy() {
        this.unregisterPreview();
    },
    methods: {
        /**
         * Redirect to the download URL.
         */
        download() {
            if (!this.downloadUrl) {
                return;
            }

            window.open(this.downloadUrl,'_blank');
        },

        /**
         * Check whether the current route is part of the form flow.
         *
         * @returns {boolean}
         */
        inFormFlow() {
            const formRoutes = [
                'form',
                'list-form',
                'blueprint-skin-settings-form',
                'blueprint-form',
                'blueprint-block-form',
            ];

            return formRoutes.includes(this.$route.name);
        },

        /**
         * Open the media inspector.
         */
        openMediaInspector() {
            if (!this.inFormFlow()) {
                this.$router.push({
                    name: 'media-inspector',
                    params: {
                        ...this.$route.params,
                        mediaId: this.objectId,
                    },
                });

                return;
            }

            const params = {
                ...this.$route.params,
            };

            // If there is no relationString present currently and we have a baseRelationString given
            // We use that one
            if (!params.relationString && this.baseRelationString) {
                params.relationString = this.baseRelationString;
            } else if (this.baseRelationString) {
                params.relationString += `:${this.baseRelationString}`;
            }

            params.relationString = params.relationString || '';
            params.relationString += `:media-inspector:${this.objectId}`;

            //redirect to the new relation state
            this.$router.push({
                name: this.$route.name,
                params,
                query: this.$route.query,
            });
        },

        /**
         * Return a normalized file extension.
         *
         * @returns {string}
         */
        getFileExtension() {
            return this.extension
                .replace(/^\./, '')
                .toLowerCase();
        },

        /**
         * Determine how GLightbox should render the media.
         *
         * @returns {'image'|'video'|'external'|null}
         */
        getPreviewType() {
            const extension = this.getFileExtension();

            const videoExtensions = [
                'mp4',
                'ogg',
                'webm',
                'mov',
            ];

            if (this.isImage === '1') {
                return 'image';
            }

            if (videoExtensions.includes(extension)) {
                return 'video';
            }

            if (extension === 'pdf') {
                return 'external';
            }

            return null;
        },
        /**
         * Check if we can show a preview for this media item
         * @return bool
         */
        canPreview() {
            return this.fileExists === "1" && this.getPreviewType() && this.downloadUrl && this.objectId
        },
        /**
         * Build the preview item for this media card.
         *
         * @returns {object|null}
         */
        getPreviewItem() {
            if (! this.canPreview()) {
                return null;
            }

            return {
                objectId: this.objectId,
                href: this.previewUrl,
                type: this.getPreviewType(),
                title: this.title,
                description: this.body,
            };
        },

        /**
         * Register this media card in its shared preview gallery.
         */
        registerPreview() {
            if (!this.previewGalleryId) {
                return;
            }

            const item = this.getPreviewItem();

            if (!item) {
                MediaCardPreviewGallery.unregister(
                    this.previewGalleryId,
                    this.objectId,
                );

                return;
            }

            MediaCardPreviewGallery.register(
                this.previewGalleryId,
                item,
            );
        },

        /**
         * Remove this media card from its shared preview gallery.
         */
        unregisterPreview() {
            if (!this.previewGalleryId) {
                return;
            }

            MediaCardPreviewGallery.unregister(
                this.previewGalleryId,
                this.objectId,
            );
        },

        /**
         * Open the media preview.
         *
         * Items with a previewGalleryId open inside the shared gallery.
         * Items without a previewGalleryId open as a standalone preview.
         *
         * @param {Event|null} event
         */
        preview(event) {
            const item = this.getPreviewItem();

            if (!item) {
                return;
            }

            if (this.previewGalleryId) {
                /*
                 * Ensure the latest prop values are registered before opening.
                 */
                MediaCardPreviewGallery.register(
                    this.previewGalleryId,
                    item,
                );

                MediaCardPreviewGallery.open(
                    this.previewGalleryId,
                    this.objectId,
                );

                return;
            }

            MediaCardPreviewGallery.openSingle(item);
        },
    },
}, 'cms/media/cms-media-card.html');